import { $, $$, pageData } from '../lib/dom.js';
import { ct } from '../lib/i18n.js';
import { formToObject } from '../lib/form.js';
import { api } from '../lib/api.js';
import { trackDirtyForm } from '../lib/dirty-form.js';
import { mountRolePicker, type RoleOption, type RolePicker } from '../lib/role-picker.js';
import { findSelectMenu, upgradeSelects } from '../lib/select-menu.js';

interface SettingsPageData {
  managerRoles: { id: string; name: string; color: number }[];
  roles: RoleOption[];
}

export function initSettingsPage(): void {
  const form = $<HTMLFormElement>('[data-settings-form]');
  if (!form) return;

  const guildId = form.dataset.guildId ?? '';
  const isPremium = form.dataset.premium === 'true';

  /*
   * Saving is armed **first**.
   *
   * It used to be last, after reading the data block and mounting the role
   * picker. Yet those two steps can fail on unexpected data - and the page then
   * became silently impossible to save, with nothing to say so. Saving is the
   * essential function of this page: it must depend on no widget at all.
   */
  let rolePicker: RolePicker | null = null;
  let access: CommandAccess | null = null;

  trackDirtyForm({
    form,
    successMessage: ct('settingsSaved', 'Settings saved.'),
    onSave: async () => {
      const values = formToObject(form) as {
        language: string;
        prefix: string;
        timezone: string;
        embedColors: Record<string, string>;
      };

      await api.patch(`/api/settings?guildId=${guildId}`, {
        guildId,
        language: values.language,
        prefix: values.prefix,
        timezone: values.timezone,
        embedColors: values.embedColors,
        // A checkbox is absent from `formToObject` when it is unchecked, and an
        // absent field means "leave it as it is" to the API: read it directly,
        // so switching the page off actually switches it off.
        publicProfile:
          form.querySelector<HTMLInputElement>('[name="publicProfile"]')?.checked ?? true,
      });

      // The manager roles have their own route (premium check on the server):
      // we only call it when the subscription is active, to avoid a needless 402.
      if (isPremium && rolePicker) {
        await api.put(`/api/settings/manager-roles?guildId=${guildId}`, {
          guildId,
          roleIds: rolePicker.value,
        });
      }

      /*
       * The command rules, and **only the ones that moved**.
       *
       * Sixty-five rules sent on every save of a prefix is sixty-five writes
       * for a change to none of them. The page knows what it rendered, so it
       * can tell the difference; an untouched command keeps having no document
       * at all, which is what the service is built to do.
       */
      const changed = access?.changed() ?? [];

      if (changed.length > 0) {
        await api.put(`/api/command-access?guildId=${guildId}`, { guildId, rules: changed });
        access?.commit();
      }
    },
  });

  const { managerRoles, roles } = pageData<SettingsPageData>();

  // ─── Live colour preview ─────────────────────────────────────────────────
  for (const colorInput of $$<HTMLInputElement>('[data-color-input]', form)) {
    const key = colorInput.dataset.colorInput ?? '';
    const textInput = $<HTMLInputElement>(`[data-color-text="${key}"]`, form);
    const preview = $(`[data-color-preview="${key}"]`, form);

    const sync = (value: string) => {
      preview?.style.setProperty('border-left-color', value);
      if (textInput && textInput.value.toUpperCase() !== value.toUpperCase()) {
        textInput.value = value.toUpperCase();
      }
    };

    colorInput.addEventListener('input', () => sync(colorInput.value));

    // Manual input is only reflected once it forms a valid colour.
    textInput?.addEventListener('input', () => {
      const value = textInput.value.trim();
      if (/^#[0-9a-fA-F]{6}$/.test(value)) {
        colorInput.value = value;
        preview?.style.setProperty('border-left-color', value);
      }
    });

    sync(colorInput.value);
  }

  // ─── Manager roles ───────────────────────────────────────────────────────
  rolePicker = mountRolePicker(
    form,
    roles,
    managerRoles.map((role) => role.id),
  );

  // ─── Command access ──────────────────────────────────────────────────────
  access = mountCommandAccess(form);
}

/* ── Who may run which command ───────────────────────────────────────────── */

interface AccessRule {
  name: string;
  enabled: boolean;
  allowedRoleIds: string[];
  blockedRoleIds: string[];
  allowedChannelIds: string[];
}

interface CommandAccess {
  /** The rules that differ from what the server rendered. */
  changed: () => AccessRule[];
  /** Takes the current state as the new reference, after a successful save. */
  commit: () => void;
}

const ids = (value: string | undefined): string[] => (value ?? '').split(',').filter(Boolean);

/**
 * The command table, folded up until somebody asks for it.
 *
 * Sixty-five commands, each with three role-and-channel pickers, used to be
 * written out in full: a hundred and ninety-five native `<select>` elements
 * carrying the server's whole role list apiece. It was slow to parse, it
 * stretched the layout before the components replaced it, and it was drawn for
 * every command whether or not anybody was looking at one.
 *
 * The groups are closed, and a group's pickers are built the moment it opens —
 * from the lists published once at the bottom of the page.
 */
function mountCommandAccess(form: HTMLFormElement): CommandAccess | null {
  const section = $<HTMLElement>('#command-access', form);
  if (!section) return null;

  const rows = $$<HTMLElement>('[data-command-rule]', section);

  const read = (row: HTMLElement): AccessRule => {
    /*
     * From the mounted picker when there is one, from the attributes when
     * there is not: a group nobody opened still has to save what it holds,
     * rather than the empty value of a widget that was never built.
     */
    const chosen = (field: string, fallback: string | undefined): string[] =>
      findSelectMenu(row, field)?.value ?? ids(fallback);

    return {
      name: row.dataset.commandRule ?? '',
      enabled: row.querySelector<HTMLInputElement>('[data-field="enabled"]')?.checked ?? true,
      allowedRoleIds: chosen('allowedRoleIds', row.dataset.allowedRoles),
      blockedRoleIds: chosen('blockedRoleIds', row.dataset.blockedRoles),
      allowedChannelIds: chosen('allowedChannelIds', row.dataset.allowedChannels),
    };
  };

  let reference = new Map(rows.map((row) => [row, JSON.stringify(read(row))]));

  const build = (group: HTMLElement) => {
    let added = false;

    for (const row of $$<HTMLElement>('[data-command-rule]', group)) {
      const host = row.querySelector<HTMLElement>('[data-access-pickers]');
      if (!host || host.childElementCount > 0) continue;

      const fields = [
        { field: 'allowedRoleIds', source: 'roles', label: section.dataset.labelAllowedRoles },
        { field: 'blockedRoleIds', source: 'roles', label: section.dataset.labelBlockedRoles },
        { field: 'allowedChannelIds', source: 'channels', label: section.dataset.labelChannels },
      ] as const;

      const selected: Record<string, string | undefined> = {
        allowedRoleIds: row.dataset.allowedRoles,
        blockedRoleIds: row.dataset.blockedRoles,
        allowedChannelIds: row.dataset.allowedChannels,
      };

      for (const { field, source, label } of fields) {
        const select = document.createElement('select');
        select.multiple = true;
        select.className = 'select select-bordered select-sm w-full';
        select.dataset.selectMenu = '';
        select.dataset.selectSource = source;
        select.dataset.field = field;
        select.dataset.selected = selected[field] ?? '';
        select.dataset.placeholder = label ?? '';
        select.setAttribute('aria-label', `${label ?? ''} · ${row.dataset.commandRule ?? ''}`);
        host.append(select);
      }

      added = true;
    }

    /*
     * Upgraded from the document, not from the group: the option lists live in
     * a block at the end of the page, and a component built from the group
     * alone would find no roles to offer — which is precisely the defect the
     * shared lists were introduced to fix elsewhere.
     */
    if (added) upgradeSelects(document);
  };

  for (const group of $$<HTMLDetailsElement>('[data-access-group]', section)) {
    group.addEventListener('toggle', () => {
      if (group.open) build(group);
    });
  }

  filterCommands(section, build);

  return {
    changed: () =>
      rows
        .map((row) => ({ row, rule: read(row) }))
        .filter(({ row, rule }) => JSON.stringify(rule) !== reference.get(row))
        .map(({ rule }) => rule),

    commit: () => {
      reference = new Map(rows.map((row) => [row, JSON.stringify(read(row))]));
    },
  };
}

/**
 * Finding one command among sixty-five.
 *
 * A group holding a match is opened — and opening it is what builds its
 * pickers, so a search lands on a row that can be edited straight away rather
 * than on a heading.
 */
function filterCommands(section: HTMLElement, build: (group: HTMLElement) => void): void {
  const input = section.querySelector<HTMLInputElement>('[data-access-filter]');
  if (!input) return;

  // Inside a form, Enter submits — which here means saving. A search field is
  // not a save button.
  input.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') event.preventDefault();
  });

  input.addEventListener('input', () => {
    const query = input.value.trim().toLowerCase();

    for (const group of $$<HTMLDetailsElement>('[data-access-group]', section)) {
      let visible = 0;

      for (const row of $$<HTMLElement>('[data-command-rule]', group)) {
        const match = query === '' || (row.dataset.search ?? '').includes(query);
        row.classList.toggle('hidden', !match);
        if (match) visible += 1;
      }

      group.classList.toggle('hidden', visible === 0);

      if (query !== '' && visible > 0 && !group.open) {
        group.open = true;
        build(group);
      }
    }
  });
}
